import { alpha, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { addGeofenceCheckedId, setFilterTab } from 'app/actions/map';
import { MapTransportConditionIconsList, MapTransportTaskList } from 'app/pages/Map';
import { useAsyncAction } from 'app/services';
import { transportTaskLoad } from 'app/store/map';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tooltip } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';

const MapLeafletTransportTooltip = function (props) {
  const dispatch = useDispatch();
  const { value, datetime, ...rest } = props;
  const { zoom, ref: mapRef, filterTab, geofenceCheckedId } = useSelector((state) => state.map);
  const [infoOpen, setInfoOpen] = useState(false);

  const { asyncAction: fetchData, isLoading: isFetchLoading } = useAsyncAction(transportTaskLoad);

  const maxWidth = useMemo(() => {
    return config.mapTransportTaskColumns.reduce((accumulator, column) => accumulator + column.width, 24);
  }, []);

  const handleItemColumnClick = useCallback(
    (value) => {
      if (geofenceCheckedId.indexOf(value.geofence_id) === -1) {
        dispatch(addGeofenceCheckedId(value.geofence_id));
      }

      if (filterTab !== 'geofence') {
        dispatch(setFilterTab('geofence'));
      }

      mapRef.current.handleClosePopup();
      mapRef.current.handleFlyTo([value.latitude, value.longitude]);
    },
    [mapRef, filterTab, geofenceCheckedId]
  );

  useEffect(() => {
    if (infoOpen) {
      fetchData(value.id);
    }
  }, [infoOpen]);

  return (
    <Tooltip direction='bottom' className='leaflet-tooltip-bottom-transport' maxWidth={maxWidth} {...rest}>
      <Stack
        direction='column'
        sx={(theme) => ({
          bgcolor: 'background.paper',
          overflow: 'hidden',
          borderRadius: theme.shape.borderRadius,
        })}>
        <Stack
          direction='row'
          justifyContent='center'
          onClick={() => setInfoOpen(!infoOpen)}
          sx={{
            fontSize: '12px',
            position: 'relative',
            '&:hover': {
              bgcolor: 'background.default',
            },
          }}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='center'
            sx={(theme) => ({
              padding: '4px 6px',
              color: theme.palette.common.black,
              ...(infoOpen && {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }),
              '.MuiTypography-root': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'center',
                fontSize: '12px',
              },
            })}>
            <Typography variant='body1'>{value.name}</Typography>
          </Stack>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='center'
            sx={(theme) => ({
              padding: '4px 6px',
              marginLeft: 'auto',
              div: {
                display: 'flex',
                marginRight: '2px',
                svg: {
                  fontSize: '18px',
                },
              },
              'div:last-of-type': {
                marginRight: theme.spacing(0),
              },
              button: {
                padding: '2px',
                svg: {
                  fontSize: '20px',
                },
              },
            })}>
            <MapTransportConditionIconsList
              data={value}
              history={false}
              datetime={datetime}
              zoom={zoom === config.mapMinTransportTooltipZoom}
              conditions={value.conditions}
            />
          </Stack>
        </Stack>
        {infoOpen && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              fontSize: '12px',
              bgcolor: 'background.paper',
              minWidth: maxWidth,
            }}>
            <Stack
              direction='column'
              sx={(theme) => ({
                padding: theme.spacing(2),
              })}>
              {!isFetchLoading && value.task && (
                <React.Fragment>
                  <Stack
                    direction='row'
                    sx={(theme) => ({
                      display: 'grid',
                      gap: 2,
                      marginBottom: theme.spacing(1),
                      '.MuiPaper-root': {
                        position: 'relative',
                        border: 'none',
                        display: 'grid',
                        gap: '6px',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                      },
                      '.MuiPaper-row': {
                        display: 'grid',
                        gap: 1,
                        gridTemplateColumns: 'repeat(6, 1fr)',
                        '.MuiTypography-root:first-of-type': {
                          color: 'text.secondary',
                        },
                      },
                      '.MuiPaper-row .MuiTypography-root:first-of-type': {
                        gridRow: '1',
                        gridColumn: '1/4',
                      },
                      '.MuiPaper-row .MuiTypography-root:last-of-type': {
                        gridRow: '1',
                        gridColumn: '4/7',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        'span:nth-of-type(2)': {
                          paddingLeft: theme.spacing(0.5),
                        },
                      },
                    })}>
                    <Paper variant='outlined'>
                      <Stack className='MuiPaper-column'>
                        <Stack className='MuiPaper-row'>
                          <Typography variant='body1'>Номер задания</Typography>
                          <Typography variant='body1'>{value.task.id}</Typography>
                        </Stack>
                        <Stack className='MuiPaper-row'>
                          <Typography variant='body1'>Статус задания</Typography>
                          <Typography variant='body1'>
                            {
                              {
                                finished: 'Завершено',
                                active: 'Активно',
                              }[value.task.status]
                            }
                          </Typography>
                        </Stack>
                      </Stack>
                      <Stack className='MuiPaper-column'>
                        <Stack className='MuiPaper-row'>
                          <Typography variant='body1'>Дата начала</Typography>
                          <Typography variant='body1'>{moment(value.task.task_date).format('DD.MM.YYYY HH:mm:ss')}</Typography>
                        </Stack>
                        <Stack className='MuiPaper-row'>
                          <Typography variant='body1'>Дата завершения</Typography>
                          <Typography variant='body1'>
                            {moment(value.task.finished_date).isValid() && moment(value.task.finished_date).format('DD.MM.YYYY HH:mm:ss')}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Stack>
                  <MapTransportTaskList value={value} onItemColumnClick={handleItemColumnClick} />
                </React.Fragment>
              )}
              {!isFetchLoading && !value.task && (
                <Stack
                  direction='column'
                  alignItems='center'
                  justifyContent='center'
                  sx={(theme) => ({
                    padding: theme.spacing(2),
                    '.MuiTypography-root': {
                      textAlign: 'center',
                    },
                  })}>
                  <Typography align='center' variant='body1' component='span'>
                    Здесь пока ничего нет
                  </Typography>
                  <Typography align='center' color='text.secondary' variant='caption' component='span'>
                    В этом разделе будет находиться текущее задание транспорта
                  </Typography>
                </Stack>
              )}
              {isFetchLoading && (
                <Stack
                  direction='row'
                  justifyContent='center'
                  sx={(theme) => ({
                    width: '100%',
                    height: '100%',
                    padding: theme.spacing(2),
                  })}>
                  <CircularProgress size={18} />
                </Stack>
              )}
            </Stack>
          </Stack>
        )}
        <Stack
          direction='column'
          alignItems='center'
          justifyContent='center'
          sx={(theme) => ({
            display: 'none',
            fontSize: '12px',
            bgcolor: 'background.default',
            cursor: 'pointer',
            color: theme.palette.text.secondary,
            '&:hover': {
              bgcolor: alpha(theme.palette.grey.main, 0.12),
            },
          })}>
          <Typography variant='body1'>Свернуть</Typography>
        </Stack>
      </Stack>
    </Tooltip>
  );
};

export default MapLeafletTransportTooltip;
